import React, { useState, useMemo, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	setIDsRestaurant: (value: string[]) => void;
	restaurants: IItemForGetShortInfoWithStatus[];
	IDsSelectedRestaurant: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ setIDsRestaurant, restaurants, IDsSelectedRestaurant }) => {
		const [searchString, setSearchString] = useState<string>('');
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		const visibleData = useMemo(() => {
			const items = showArchived
				? restaurants
				: restaurants.filter(
						(el) => el.status === EntityStatus.Active || IDsSelectedRestaurant.includes(el.id),
				  );

			return items.map((el) => ({
				...el,
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [restaurants, showArchived, IDsSelectedRestaurant]);

		const filteredData = useMemo(() => {
			return searchString
				? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleData;
		}, [searchString, visibleData]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const handleSelectAllData = useCallback(() => {
			if (visibleData.length === IDsSelectedRestaurant.length) {
				setIDsRestaurant([]);
			} else {
				const IDsOfAllRestaurants = visibleData.map((el) => el.id);
				setIDsRestaurant(IDsOfAllRestaurants);
			}
		}, [visibleData, IDsSelectedRestaurant, setIDsRestaurant]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={!!(visibleData.length && visibleData.length === IDsSelectedRestaurant.length)}
								disabled={!visibleData.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllRestaurantsLabel"
									asFragment={true}
								/>
							</Checkbox>
							<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
								<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[
				visibleData,
				IDsSelectedRestaurant,
				handleSelectAllData,
				searchString,
				setSearchString,
				showArchived,
				toggleShowArchived,
			],
		);

		return (
			<OutsideFormSelect
				testId="RestaurantsMultipleSelect"
				name="IDsSelectedRestaurant"
				label="restaurantsLabel"
				value={IDsSelectedRestaurant}
				options={filteredData}
				onChange={setIDsRestaurant}
				mode="multiple"
				counterValue={IDsSelectedRestaurant.length}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
